import React, { ReactElement, useEffect, useState } from 'react';
import '../../App.css';
import moment, { Moment } from 'moment';
import {
  Button, DatePicker, InputNumber, Modal, Space,
} from 'antd';
import { toast } from 'react-toastify';
import { setPmState } from '../../store/entities/pm';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { DATETIME_FORMAT } from '../../util/constants';
import { DayOfWeek } from '../../types/DayOfWeek';
import { DAYS_OF_WEEK } from '../../util/tstamp';
import DayOfWeekSelector from './DayOfWeekSelector';

const { RangePicker } = DatePicker;

const InputBarPage = (): ReactElement => {
  const dispatch = useAppDispatch();
  // user input variables
  const startDateFromUrl = useAppSelector((state) => state.entities.pm.startDate);
  const endDateFromUrl = useAppSelector((state) => state.entities.pm.endDate);
  const thresholdFromStore = useAppSelector((state) => state.entities.pm.threshold);
  const organizationName = useAppSelector((state) => state.entities.pm.organizationName);
  const selectedIntersections = useAppSelector((state) => (
    state.entities.pm.selectedIntersections));
  const isDataLoading = useAppSelector((state) => state.entities.pm.isDataLoading);

  const [startDate, setStartDate] = useState(startDateFromUrl);
  const [endDate, setEndDate] = useState(endDateFromUrl);

  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<DayOfWeek[]>(
    DAYS_OF_WEEK.filter((d) => d.defaultSelected),
  );
  // const [indeterminate, setIndeterminate] = useState(true);
  // const [checkAll, setCheckAll] = useState(false);
  const [threshold, setThreshold] = useState(thresholdFromStore);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // functions
  const submitInput = () => {
    if (selectedDaysOfWeek.length > 0) {
      dispatch(setPmState({
        submitted: true,
      }));
    } else {
      toast.error('Select at least one day of week');
    }
  };

  const onThresholdChange = (n: number) => {
    setThreshold(n);
    dispatch(setPmState({ threshold: n }));
  };

  // These just exist to keep tslint happy
  const setModalVisible = () => setIsModalVisible(true);
  const setModalInvisible = () => setIsModalVisible(false);

  const onDateChange = (dates: unknown) => {
    // eslint-disable-next-line no-undef
    const [start, end] = (dates as [Moment, Moment]);
    const startFormatted = start.format(DATETIME_FORMAT);
    const endFormatted = end.endOf('day')
      .format(DATETIME_FORMAT);
    setStartDate(startFormatted);
    setEndDate(endFormatted);
    dispatch(setPmState({
      startDate: startFormatted,
      endDate: endFormatted,
    }));
  };

  const onSelectedDaysOfWeekChange = (selected: DayOfWeek[]) => {
    setSelectedDaysOfWeek(selected.slice()
      .sort((a, b) => a.value - b.value));
  };

  useEffect(() => {
    // const checkedDowNumberList = selectedDaysOfWeek
    // .map((key) => DAY_OF_WEEK_NUMBER_MAP[key]);

    dispatch(setPmState({
      selectedDaysOfWeek,
    }));
  }, [selectedDaysOfWeek]);

  // const handleCheckDow = list => {
  //     setSelectedDaysOfWeek(list);
  //     setIndeterminate(!!list.length && list.length < DAY_OF_WEEK_OPTIONS.length);
  //     setCheckAll(list.length === DAY_OF_WEEK_OPTIONS.length);
  // };

  // const handleCheckAll = e => {
  //     setSelectedDaysOfWeek(e.target.checked ? DAY_OF_WEEK_OPTIONS : []);
  //     setIndeterminate(false);
  //     setCheckAll(e.target.checked);
  // };

  const disabledDate = (current: Moment) => {
    const AVAILABLE_YEAR_MONTH = ['201907', '202001', '202006', '202007', '202008', '202009', '202104', '202105', '202106', '202107', '202108', '202109', '202110'];
    const YEAR_MONTH_FORMAT = 'YYYYMM';
    const year = current.format('YYYY');
    const month = current.format('MM');
    const isAvailable = AVAILABLE_YEAR_MONTH.reduce((acc: boolean, yearMonth) => {
      if (acc) return acc;
      const availableYear = moment(yearMonth, YEAR_MONTH_FORMAT)
        .format('YYYY');
      const availableMonth = moment(yearMonth, YEAR_MONTH_FORMAT)
        .format('MM');
      if (year === availableYear && month === availableMonth) return true;
      return acc;
    }, false);
    if (current.isAfter('2022-01-01') && current.isBefore(moment()
      .subtract(1, 'day'))) {
      return false;
    }
    // for (const yearMonth of AVAILABLE_YEAR_MONTH) {
    // }
    return !isAvailable;
  };

  // @ts-ignore
  return (
    <>
      <Space wrap>
        <RangePicker
          value={[moment(startDate), moment(endDate)]}
          style={{ flex: '1 1 0' }}
          disabledDate={disabledDate}
          // Subtracting one day from these because we'll only have data for the previous day.
          // This prevents 'this week' being set to no days in case it is currently Sunday,
          // and other similar edge cases
          ranges={{
            'This Week': [moment()
              .subtract(1, 'day')
              .startOf('week'),
            moment()
              .subtract(1, 'day')],
            'Last Week': [
              moment()
                .subtract(1, 'day')
                .subtract(1, 'week')
                .startOf('week'),
              moment()
                .subtract(1, 'day')
                .subtract(1, 'week')
                .endOf('week'),
            ],
            'This Month': [moment()
              .subtract(1, 'day')
              .startOf('month'),
            moment()
              .subtract(1, 'day')],
            'Last Month': [
              moment()
                .subtract(1, 'day')
                .subtract(1, 'month')
                .startOf('month'),
              moment()
                .subtract(1, 'day')
                .subtract(1, 'month')
                .endOf('month'),
            ],
            'August 2021': [moment('aug 2021')
              .startOf('month'), moment('aug 2021')
              .endOf('month')],
            'August 2020': [moment('aug 2020')
              .startOf('month'), moment('aug 2020')
              .endOf('month')],
          }}
          onChange={onDateChange}
        />
        <Space>
          <Button onClick={setModalVisible}>Options</Button>
          <Button
            type="primary"
            disabled={organizationName === 'Default' || organizationName === null || !selectedIntersections.length}
            loading={isDataLoading}
            onClick={submitInput}
          >
            Submit
          </Button>
        </Space>
      </Space>
      <Modal
        visible={isModalVisible}
        onOk={setModalInvisible}
        title="Heatmap Options"
        width={500}
        onCancel={setModalInvisible}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space direction="horizontal">
            <span className="input-label">Threshold</span>
            <InputNumber
              style={{ width: '80px' }}
              type="number"
              min={0}
              max={100}
              value={threshold}
              step={1}
              onChange={onThresholdChange}
            />
            <div>trips</div>
          </Space>
          <Space direction="vertical">
            <span className="input-label">Days of week</span>
            <DayOfWeekSelector value={selectedDaysOfWeek} onChange={onSelectedDaysOfWeekChange} />
            {/* <Select */}
            {/*  style={{ width: '300px' }} */}
            {/*  mode="multiple" */}
            {/*  placeholder="Days of week" */}
            {/*  defaultValue={DAYS_OF_WEEK */}
            {/*    .filter((e) => e.defaultSelected) */}
            {/*    .map((e) => e.value)} */}
            {/*  onChange={onSelectedDaysOfWeekChange} */}
            {/* > */}
            {/*  {DAYS_OF_WEEK.map((e) => ( */}
            {/*    <Option */}
            {/*      defaultSelected={e.defaultSelected} */}
            {/*      key={e.number} */}
            {/*      value={e.number} */}
            {/*    > */}
            {/*      {e.abbreviation} */}
            {/*    </Option> */}
            {/*  ))} */}
            {/* </Select> */}
          </Space>
        </Space>
      </Modal>
    </>
  );
};
export default InputBarPage;
